import React, {useEffect, useReducer} from 'react';
import {useIntl} from 'react-intl';
import { Lx } from '../../../../../../i18n/consts';
import Selector from '../../../../../../components/shared/nextgen/selector';
import {FilterActionType, FilterChangeAction, FilterState} from '../filter/types';
import {REmployees} from '../../../../../../features/roles/types';
import CostFilterListPreview from './filter-list-preview';
import { useAppSelector } from '../../../../../../hooks/hooks';
import PropertyFilterPopup from '../../../../../../components/shared/property-filter-popup';

interface Props {
    employees: REmployees[];
    onChange: (filter: FilterState) => void;
    disabled?: boolean;
    disableEmployeesFilter?: boolean;
}

interface State {
    draft: FilterState;
    current: FilterState;
}

export default function CostFilterListPopup(props: Props) {
    const intl = useIntl();
    const [selectedFiltersCount, setSelectedFiltersCount] = React.useState(0);
    const filter = useAppSelector((selector) => selector.timeTrackingFilters.filter.propertyFilter);

    const emptyFilterState: FilterState = {
        selectedEmployees: []
    }

    const initialFilterState: FilterState = {
        ...emptyFilterState,
    }

    const initialState: State = {
        current: initialFilterState,
        draft: initialFilterState,
    }

    const handlePropertyChange = (action: FilterActionType, state: FilterState, payload: any): FilterState => {
        switch (action) {
            case 'EMPLOYEES_UPDATE':
                state.selectedEmployees = payload ?? [];
                break;
        }

        return state;
    }

    const reducer = (state: State, action: Partial<FilterChangeAction>): State => {
        const nextState = {...state};
        switch (action.action) {
            case 'HYDRATE': {
                const incoming = (action.payload as FilterState) ?? emptyFilterState;
                nextState.current = {...incoming};
                nextState.draft = {...incoming};
                break;
            }
            case 'APPLY':
                nextState.current = {...state.draft};
                break;
            case 'CANCEL':
                nextState.draft = {...state.current};
                break;
            case 'RESET':
                nextState.draft = {...emptyFilterState};
                break;
            default:
                nextState.draft = handlePropertyChange(action.action as FilterActionType, nextState.draft, action.payload);
                break;
        }

        const currentFilters = Object.keys(nextState.current).map((x) => (nextState.current[x as keyof FilterState]));

        setSelectedFiltersCount(props.disableEmployeesFilter ? 0 : Object.values(currentFilters).filter((x) => x.length > 0).length);

        return nextState;
    }

    const [filterState, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        dispatch({ action: 'HYDRATE', payload: filter });
    }, [filter]);

    const resetFilter = () => {
        dispatch({ action: 'RESET' })
    }

    const applyFilter = () => {
        dispatch({ action: 'APPLY' });
        props.onChange(filterState.draft);
    }

    const handleClose = () => {
        dispatch({ action: 'CANCEL' });
    };

    return (
        <PropertyFilterPopup
            selectedFiltersCount={selectedFiltersCount}
            previewContent={<CostFilterListPreview selectedEmployees={props.disableEmployeesFilter ? [] : filterState.current.selectedEmployees} />}
            onReset={resetFilter}
            onApply={applyFilter}
            onCancel={handleClose}
            disabled={props.disabled}
        >
            {!props.disableEmployeesFilter && (
                <Selector id={'employeeFilter'}
                    label={Lx.General.EMPLOYEES}
                    items={props.employees.map((employee) => ({ id: employee.id, name: employee.name, originalId: employee.id }))}
                    name={Lx.General.EMPLOYEES}
                    selected={filterState.draft.selectedEmployees}
                    maxWidth={368}
                    setSelectedValues={(values) => dispatch({ action: 'EMPLOYEES_UPDATE', payload: values })} intl={intl}
                    disabled={props.disabled}/>
            )}
        </PropertyFilterPopup>
    )
}
